import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { DeliveryHandover, DriverJob, DriverView } from '../api/types';
import { Alert, Spinner, money } from '../components/common';

const HANDOVERS: { value: DeliveryHandover; label: string }[] = [
  { value: 'HandedToCustomer', label: 'Handed to the customer' },
  { value: 'LeftWithSomeoneElse', label: 'Left with reception / security' },
  { value: 'LeftAtDoor', label: 'Left at the door' },
];

/**
 * The driver's screen: what they are carrying, and the drop-off form.
 *
 * Built phone-first — big targets, address and phone number one tap away, and
 * the delivery closed out with a photo taken on the spot.
 */
export function DriverJobsPage() {
  const [driver, setDriver] = useState<DriverView | null | undefined>(undefined);
  const [jobs, setJobs] = useState<DriverJob[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [me, list] = await Promise.all([api.driverMe(), api.driverJobs(true)]);
      setDriver(me ?? null);
      setJobs(list);
    } catch (e) {
      setError((e as Error).message);
      setDriver((current) => (current === undefined ? null : current));
    }
  }, []);

  useEffect(() => {
    void load();

    // The shop assigns work while the driver is on the road, so the list has
    // to arrive without them thinking to refresh.
    const timer = window.setInterval(load, 30_000);
    return () => window.clearInterval(timer);
  }, [load]);

  async function toggleShift() {
    if (!driver) return;

    setBusy(true);
    setError(null);

    try {
      setDriver(await api.driverSetShift(!driver.isOnShift));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (driver === undefined) return <Spinner label="Loading your deliveries…" />;

  if (driver === null) {
    return (
      <section className="page page--narrow center">
        <h1>You&apos;re not signed up to deliver</h1>
        <Link to="/driver/signup" className="btn btn--primary">
          Apply to be a driver
        </Link>
      </section>
    );
  }

  if (!driver.canTakeDeliveries) {
    return (
      <section className="page page--narrow">
        <h1>My deliveries</h1>
        <Alert tone="info">
          {driver.status === 'PendingReview'
            ? 'Your application is still being reviewed.'
            : driver.statusReason ?? 'You are not approved to take deliveries yet.'}
        </Alert>
        <Link to="/driver/signup" className="btn btn--ghost btn--block">
          View my application
        </Link>
      </section>
    );
  }

  const live = jobs.filter((j) => !j.isDelivered);
  const done = jobs.filter((j) => j.isDelivered);

  return (
    <section className="page page--narrow">
      <h1>My deliveries</h1>

      {error && <Alert>{error}</Alert>}

      <div className="panel shift-panel">
        <div>
          <strong>{driver.isOnShift ? 'On shift' : 'Off shift'}</strong>
          <p className="muted small">
            {driver.isOnShift
              ? 'The shop can assign you deliveries.'
              : 'You will not be given new deliveries.'}
          </p>
        </div>
        <button
          type="button"
          className={driver.isOnShift ? 'btn btn--ghost' : 'btn btn--primary'}
          disabled={busy}
          onClick={toggleShift}
        >
          {driver.isOnShift ? 'Go off shift' : 'Go on shift'}
        </button>
      </div>

      {live.length === 0 && (
        <Alert tone="info">
          Nothing to deliver right now.{' '}
          {driver.isOnShift ? 'This updates on its own when the shop assigns you an order.' : 'Go on shift to be given work.'}
        </Alert>
      )}

      {live.map((job) => (
        <JobCard key={job.orderId} job={job} onDone={load} onError={setError} />
      ))}

      {done.length > 0 && (
        <div className="panel">
          <h2 className="panel__title">Delivered today</h2>
          {done.map((job) => (
            <p key={job.orderId} className="muted small">
              {job.orderNumber} — {job.customerName}, {job.suburb}
            </p>
          ))}
        </div>
      )}
    </section>
  );
}

function JobCard({
  job,
  onDone,
  onError,
}: {
  job: DriverJob;
  onDone: () => Promise<void>;
  onError: (message: string | null) => void;
}) {
  const photoInput = useRef<HTMLInputElement>(null);
  const [dropping, setDropping] = useState(false);
  const [handover, setHandover] = useState<DeliveryHandover>('HandedToCustomer');
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const address = [job.addressLine1, job.addressLine2, job.suburb, job.city].filter(Boolean).join(', ');
  const onTheRoad = job.status === 'OutForDelivery';

  function choosePhoto(file: File | undefined) {
    if (!file) return;
    if (preview) URL.revokeObjectURL(preview);
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
  }

  async function start() {
    setBusy(true);
    onError(null);

    try {
      await api.driverStartJob(job.orderId);
      await onDone();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function complete() {
    if (!photo) {
      onError('Take a photo of the handover first.');
      return;
    }

    setBusy(true);
    onError(null);

    try {
      // Location is corroboration, not a requirement: a delivery in a
      // basement with no signal is still a delivery, so a refusal or a
      // timeout here must not block the driver from closing the job.
      const coords = await currentPosition();
      await api.driverCompleteJob(job.orderId, photo, handover, note.trim() || null, coords);
      await onDone();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel job-card">
      <div className="job-card__head">
        <strong>{job.orderNumber}</strong>
        <span className={onTheRoad ? 'pill pill--good' : 'pill'}>
          {onTheRoad ? 'On the road' : job.status}
        </span>
      </div>

      <p className="job-card__address">{address || 'No address on this order'}</p>
      {job.instructions && <p className="muted small">“{job.instructions}”</p>}

      <div className="job-card__meta">
        <span>{job.customerName}</span>
        <span>
          {job.itemCount} item{job.itemCount === 1 ? '' : 's'} · {money(job.total)}
        </span>
      </div>

      <div className="job-card__actions">
        {job.customerPhone && (
          <a href={`tel:${job.customerPhone}`} className="btn btn--ghost">
            Call
          </a>
        )}
        {address && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noopener"
            className="btn btn--ghost"
          >
            Directions
          </a>
        )}
      </div>

      {!onTheRoad && (
        <button type="button" className="btn btn--primary btn--block" disabled={busy} onClick={start}>
          I&apos;ve collected this order
        </button>
      )}

      {onTheRoad && !dropping && (
        <button type="button" className="btn btn--primary btn--block" onClick={() => setDropping(true)}>
          Mark as delivered
        </button>
      )}

      {onTheRoad && dropping && (
        <div className="dropoff">
          <label className="field">
            <span>Who took it?</span>
            <select value={handover} onChange={(e) => setHandover(e.target.value as DeliveryHandover)}>
              {HANDOVERS.map((h) => (
                <option key={h.value} value={h.value}>
                  {h.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Photo of the handover</span>
            <input
              ref={photoInput}
              type="file"
              accept="image/*"
              // capture opens the camera straight away on a phone rather than
              // the gallery — the photo is meant to be taken at the door.
              capture="environment"
              onChange={(e) => choosePhoto(e.target.files?.[0])}
            />
            <span className="muted small">Required. It settles any “it never arrived” later.</span>
          </label>

          {preview && <img src={preview} alt="Delivery photo" className="dropoff__preview" />}

          <label className="field">
            <span>Note (optional)</span>
            <input
              type="text"
              value={note}
              placeholder="Left with the security guard at the gate"
              onChange={(e) => setNote(e.target.value)}
            />
          </label>

          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={busy || !photo}
            onClick={complete}
          >
            {busy ? 'Saving…' : 'Confirm delivered'}
          </button>

          <button type="button" className="btn btn--ghost btn--block" disabled={busy} onClick={() => setDropping(false)}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/** Best-effort GPS. Never rejects — a missing fix must not block a delivery. */
function currentPosition(): Promise<{ latitude: number; longitude: number } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 5_000, maximumAge: 60_000 },
    );
  });
}
